import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { spacing } from "../../ui/tokens";
import { getRiskCategories } from "../../riskService";
import {
  getRiskAppetite,
  createRiskAppetite,
  updateRiskAppetite,
  deleteRiskAppetite,
} from "../../api/riskAppetite.api";

const comparatorOptions = [
  { value: ">=", label: "Score >= Threshold" },
  { value: ">", label: "Score > Threshold" },
  { value: "<=", label: "Score <= Threshold" },
  { value: "<", label: "Score < Threshold" },
];

export default function RiskAppetiteAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const defaultGlobalConfig = useMemo(
    () => ({
      score_threshold: 10,
      comparator: ">=",
      within_label: "Within Appetite",
      breach_label: "Exceeds Appetite",
      require_escalation: false,
    }),
    []
  );
  const [globalConfig, setGlobalConfig] = useState(defaultGlobalConfig);
  const [overrides, setOverrides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [newOverride, setNewOverride] = useState({
    category: "",
    score_threshold: 10,
    comparator: ">=",
    within_label: "Within Appetite",
    breach_label: "Exceeds Appetite",
    require_escalation: false,
  });

  const load = async () => {
    if (!canManageAdminModules) return;
    setLoading(true);
    setError(null);
    try {
      const [appetiteRes, categoriesRes] = await Promise.all([
        getRiskAppetite(),
        getRiskCategories({ include_archived: true }),
      ]);
      const appetite = appetiteRes?.data || appetiteRes;
      setGlobalConfig(appetite?.global_config || defaultGlobalConfig);
      setOverrides(appetite?.category_overrides || []);
      setCategories((categoriesRes?.data || categoriesRes || []).map((c) => c.name));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unable to load appetite configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageAdminModules]);

  const availableCategories = useMemo(() => {
    const used = new Set((overrides || []).map((o) => o.category));
    return (categories || []).filter((c) => !used.has(c));
  }, [categories, overrides]);

  const handleSaveGlobal = async () => {
    setSaving(true);
    setError(null);
    try {
      if (globalConfig?.id) {
        await updateRiskAppetite(globalConfig.id, globalConfig);
      } else {
        const payload = { ...defaultGlobalConfig, ...globalConfig, category: null };
        await createRiskAppetite(payload);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save global appetite.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOverride = async () => {
    if (!newOverride.category) return;
    setSaving(true);
    setError(null);
    try {
      await createRiskAppetite(newOverride);
      setNewOverride({
        category: "",
        score_threshold: 10,
        comparator: ">=",
        within_label: "Within Appetite",
        breach_label: "Exceeds Appetite",
        require_escalation: false,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to create override.");
    } finally {
      setSaving(false);
    }
  };

  if (!canManageAdminModules) {
    return (
      <div style={{ padding: spacing.lg * 2 }}>
        <h2 style={{ marginTop: 0 }}>Risk Appetite</h2>
        <p style={{ color: "#6b7280" }}>Requires Manage access to Admin Module Portal.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: spacing.lg * 2 }}>
      <h2 style={{ marginTop: 0 }}>Risk Appetite</h2>
      <p style={{ color: "#6b7280" }}>
        Appetite defines the maximum acceptable residual risk.
      </p>

      {loading && <div className="loading">Loading...</div>}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Global Appetite</h3>
        <div style={gridStyle}>
          <label style={fieldLabel}>
            <span style={labelText}>Threshold</span>
            <Input
              type="number"
              value={globalConfig?.score_threshold ?? 10}
              onChange={(e) =>
                setGlobalConfig((prev) => ({ ...prev, score_threshold: Number(e.target.value) }))
              }
            />
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Comparator</span>
            <Select
              value={globalConfig?.comparator ?? ">="}
              onChange={(e) =>
                setGlobalConfig((prev) => ({ ...prev, comparator: e.target.value }))
              }
            >
              {comparatorOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Within Label</span>
            <Input
              value={globalConfig?.within_label ?? "Within Appetite"}
              onChange={(e) =>
                setGlobalConfig((prev) => ({ ...prev, within_label: e.target.value }))
              }
            />
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Breach Label</span>
            <Input
              value={globalConfig?.breach_label ?? "Exceeds Appetite"}
              onChange={(e) =>
                setGlobalConfig((prev) => ({ ...prev, breach_label: e.target.value }))
              }
            />
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Require escalation on breach</span>
            <Select
              value={globalConfig?.require_escalation ? "true" : "false"}
              onChange={(e) =>
                setGlobalConfig((prev) => ({ ...prev, require_escalation: e.target.value === "true" }))
              }
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          </label>
        </div>
        <Button onClick={handleSaveGlobal} variant="primary" disabled={saving}>
          {saving ? "Saving..." : "Save Global Appetite"}
        </Button>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Category Overrides</h3>
        <div style={gridStyle}>
          <label style={fieldLabel}>
            <span style={labelText}>Category</span>
            <Select
              value={newOverride.category}
              onChange={(e) => setNewOverride((prev) => ({ ...prev, category: e.target.value }))}
            >
              <option value="">Select category</option>
              {availableCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Threshold</span>
            <Input
              type="number"
              value={newOverride.score_threshold}
              onChange={(e) =>
                setNewOverride((prev) => ({ ...prev, score_threshold: Number(e.target.value) }))
              }
            />
          </label>
          <label style={fieldLabel}>
            <span style={labelText}>Comparator</span>
            <Select
              value={newOverride.comparator}
              onChange={(e) =>
                setNewOverride((prev) => ({ ...prev, comparator: e.target.value }))
              }
            >
              {comparatorOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </label>
        </div>
        <Button onClick={handleCreateOverride} variant="secondary" disabled={saving || !newOverride.category}>
          Add Override
        </Button>

        {overrides.length === 0 && (
          <div style={{ marginTop: spacing.md, color: "#6b7280" }}>No overrides configured.</div>
        )}
        {overrides.map((override) => (
          <div key={override.id} style={{ marginTop: spacing.md, padding: spacing.md, border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <div style={{ fontWeight: 600 }}>{override.category}</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Threshold {override.comparator} {override.score_threshold}
            </div>
            <Button
              variant="destructive"
              onClick={() => setConfirmRemove(override)}
              style={{ marginTop: spacing.sm }}
            >
              Remove Override
            </Button>
          </div>
        ))}
      </div>

      <DestructiveConfirm
        actionId="RISK_APPETITE_OVERRIDE_DELETE"
        open={Boolean(confirmRemove)}
        resourceDisplayName={confirmRemove?.category}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          const pending = confirmRemove;
          setConfirmRemove(null);
          if (pending) {
            await deleteRiskAppetite(pending.id);
            await load();
          }
        }}
      />
    </div>
  );
}

const cardStyle = {
  marginTop: spacing.lg,
  padding: spacing.lg,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "white",
};

const gridStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  marginBottom: spacing.md,
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: 13,
  color: "#374151",
};
